import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Loader from "../../layout/Loader";
import Menu from "../../layout/Menu";
import Footer from "../../layout/Footer";
import axios from "axios";
import { ThreeDots } from "react-loader-spinner";

export default function DetailedDashboard() {
  let nav = useNavigate();
  let location = useLocation();

  const [detailData, setDetailData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract state data or set defaults
  const title = location.state?.title || "Dashboard Details";
  const dataType = location.state?.dataType || "users";
  const activeOnly = location.state?.activeOnly !== false; // Default to true

  useEffect(() => {
    document.getElementById("page-loader").style.display = "none";
    var element = document.getElementById("page-container");
    element.classList.add("show");

    if (location.state?.dataType) {
      fetchDetailedData();
    } else {
      // Handle legacy data format (backward compatibility)
      if (location.state?.data) {
        setDetailData(location.state.data);
        setLoading(false);
      } else {
        setError("No data available");
        setLoading(false);
      }
    }
  }, [location.state]);

  const fetchDetailedData = async () => {
    setLoading(true);
    setError(null);

    const myurl = `${process.env.REACT_APP_base_url}api/dashboard/get-dashboard-details`;

    var bodyFormData = new URLSearchParams();
    bodyFormData.append("auth_code", process.env.REACT_APP_AUTH_CODE);
    bodyFormData.append("activeOnly", activeOnly);
    bodyFormData.append("dataType", dataType);

    try {
      const response = await axios({
        method: "post",
        url: myurl,
        data: bodyFormData,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      if (response.data.success === true) {
        setDetailData(response.data.data || []);
      } else {
        setError(response.data.message || "Failed to fetch detailed data");
      }
    } catch (err) {
      setError("Network error occurred while fetching detailed data");
      console.error("Detailed dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchDetailedData();
  };

  const getTotalCount = () => {
    return detailData.reduce((sum, item) => sum + item.count, 0);
  };

  return (
    <>
      <Loader />
      <div
        id="page-container"
        className="fade page-sidebar-fixed page-header-fixed"
      >
        <Menu />
        <div id="content" className="content">
          <ol className="breadcrumb float-xl-right">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Dashboard Details</li>
          </ol>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="page-header">
              <i
                className="fa fa-arrow-left edit me-2"
                onClick={() => nav("/dashboard")}
                style={{ cursor: "pointer", paddingRight: "8px" }}
                title="Back to Dashboard"
              ></i>
              {title}
            </h1>
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fa fa-exclamation-triangle me-2"></i>
              {error}
              <button
                className="btn btn-outline-danger btn-sm ms-3"
                onClick={handleRefresh}
              >
                Try Again
              </button>
            </div>
          )}

          {/* Data Table */}
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  <i className="fa fa-table me-2" style={{ paddingRight: "8px" }}></i>
                  Company-wise Breakdown
                </h5>
                <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                  {/* Status Badge */}
                  <span className={`badge ${activeOnly ? 'bg-success' : 'bg-secondary'} fs-6 px-3 py-2`}>
                    <i className={`fa ${activeOnly ? 'fa-check-circle' : 'fa-list'} me-2`} style={{ paddingRight: "8px" }}></i>
                    {activeOnly ? 'Active Companies' : 'All Companies'}
                  </span>

                  {/* Refresh Button */}
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleRefresh}
                    disabled={loading}
                    title="Refresh Data"
                  >
                    <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''} me-2`}></i>
                    {loading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <ThreeDots
                    height="50"
                    width="50"
                    radius="9"
                    color="#007bff"
                    ariaLabel="three-dots-loading"
                    visible={true}
                  />
                  <p className="mt-3 text-muted">Loading detailed data...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th scope="col" width="10%">Sr. No</th>
                        <th scope="col" width="60%">Company Name</th>
                        <th scope="col" width="15%">Count</th>
                        <th scope="col" width="13%">Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailData.length > 0 ? (
                        detailData
                          .sort((a, b) => b.count - a.count) // Sort by count descending
                          .map((item, index) => (
                            <tr key={index} className="align-middle">
                              <td className="fw-bold text-muted">{index + 1}</td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="avatar-sm bg-light rounded-circle d-flex align-items-center justify-content-center me-2">
                                    <i className="fa fa-building text-primary" style={{ paddingRight: "8px" }}></i>
                                  </div>
                                  <span className="fw-medium">{item.name}</span>
                                </div>
                              </td>
                              <td>
                                <span className="badge bg-primary rounded-pill">
                                  {item.count.toLocaleString()}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                                  <div
                                    className="progress me-2"
                                    style={{ width: '60px', height: '8px' }}
                                  >
                                    <div
                                      className="progress-bar bg-success"
                                      style={{
                                        width: `${(item.count / getTotalCount()) * 100}%`
                                      }}
                                    ></div>
                                  </div>
                                  <small className="text-muted">
                                    {((item.count / getTotalCount()) * 100).toFixed(1)}%
                                  </small>
                                </div>
                              </td>
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="text-center py-5">
                            <div className="text-muted">
                              <i className="fa fa-inbox fa-3x mb-3 d-block" style={{ paddingRight: "8px" }}></i>
                              <h5>No Records Found</h5>
                              <p>
                                {activeOnly
                                  ? "No data available for active companies. Try switching to 'All Companies' view."
                                  : "No data available for the selected criteria."
                                }
                              </p>
                              <Link
                                to="/dashboard"
                                className="btn btn-outline-primary"
                              >
                                <i className="fa fa-arrow-left me-2" style={{ paddingRight: "8px" }}></i>
                                Back to Dashboard
                              </Link>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Table Footer */}
            {!loading && detailData.length > 0 && (
              <div className="card-footer">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex" style={{ gap: "8px" }}>
                    <small className="text-muted">
                      <i className="fa fa-chart-bar me-1" style={{ paddingRight: "4px" }}></i>
                      <strong>{getTotalCount().toLocaleString()}</strong> total records
                    </small>
                    <small className="text-muted">
                      <i className="fa fa-building me-1" style={{ paddingRight: "4px" }}></i>
                      <strong>{detailData.length}</strong> companies
                    </small>
                    <small className="text-muted">
                      <i className="fa fa-calculator me-1" style={{ paddingRight: "4px" }}></i>
                      <strong>{detailData.length > 0 ? Math.round(getTotalCount() / detailData.length) : 0}</strong> avg per company
                    </small>
                    <small className="text-muted">
                      <i className="fa fa-tag me-1" style={{ paddingRight: "4px" }}></i>
                      <strong>{dataType.charAt(0).toUpperCase() + dataType.slice(1)}</strong> data
                    </small>
                  </div>
                  <small className="text-muted">
                    <i className="fa fa-clock me-1" style={{ paddingRight: "4px" }}></i>
                    Last updated: {loading ? 'Updating...' : new Date().toLocaleString()}
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}